import type { DateTime } from "luxon"

import type { MonetaryAmount } from "../../../../currency/currencyTypes"
import { AnyMeeting } from "../../../../nonAdminUser/reports/MeetingsReceipt/UserMeetingsReceipt"
import { AnyMeetingServiceOrder } from "../../production/meetingService/components/OrderPackageSlip"

type DropdownOrder = AnyMeeting | AnyMeetingServiceOrder

type DropdownOrderExtras = Readonly<
    Partial<{
        amount: MonetaryAmount
        paymentDetails: Readonly<{ method?: string }>
        deliveryDetails: Readonly<{ time?: string }>
        sellerSupplierParty: Readonly<{ name?: string }>
        customer: Readonly<{ email?: string; displayName?: string }>
        organizers: ReadonlyArray<Readonly<{ email?: string; name?: string }>>
        kitchen: Readonly<{ name?: string }>
    }>
>

function asExtras(order: DropdownOrder): DropdownOrderExtras {
    return order as DropdownOrderExtras
}

export function hasOrganizers(order: DropdownOrder) {
    return "organizers" in order
}

export function hasCustomer(order: DropdownOrder) {
    return "customer" in order
}

export function getDeliveryTimeFromOrder(order: DropdownOrder): string | undefined {
    if ("deliveries" in order && order.deliveries?.[0]?.deliveryTime) {
        return order.deliveries[0].deliveryTime
    }
    return asExtras(order).deliveryDetails?.time
}

export function getMonetaryAmountFromOrder(order: DropdownOrder): MonetaryAmount | undefined {
    return asExtras(order).amount
}

export function getPaymentMethodFromOrder(order: DropdownOrder): string | undefined {
    return asExtras(order).paymentDetails?.method
}

export function getCanteenNameFromOrder(order: DropdownOrder): string | undefined {
    const extras = asExtras(order)
    return extras.sellerSupplierParty?.name ?? extras.kitchen?.name
}

export type DayGroup<T> = Readonly<{ day?: DateTime; items: ReadonlyArray<T> }>

// today's orders come first, the rest keep the order they arrived in
export function groupByDay<T>(
    items: ReadonlyArray<T>,
    getDay: (item: T) => DateTime | undefined,
    today: DateTime,
): ReadonlyArray<DayGroup<T>> {
    const groups = new Map<string, { day?: DateTime; items: T[] }>()
    for (const item of items) {
        const day = getDay(item)?.startOf("day")
        const key = day?.toISODate() ?? ""
        const group = groups.get(key) ?? { day, items: [] }
        group.items.push(item)
        groups.set(key, group)
    }
    const isToday = (group: DayGroup<T>) => Number(Boolean(group.day?.hasSame(today, "day")))
    return Array.from(groups.values()).sort((a, b) => isToday(b) - isToday(a))
}

export function getCustomerEmailFromOrder(order: DropdownOrder): string | undefined {
    const extras = asExtras(order)
    return extras.customer?.email ?? extras.organizers?.[0]?.email
}
